import { tokenStore } from "@/api/client";
import { toApiError } from "@/api/errors";
import { env } from "@/lib/env";
import { sessionId } from "@/lib/session";

/**
 * Multipart POST/DELETE to the API with upload progress (fetch can't report it), the same auth and
 * one silent token refresh on 401 as the generated client. `path` is relative to /api/v1.
 */
export function apiUpload<T>(
  path: string,
  form: FormData | null,
  {
    method = "POST",
    onProgress,
    signal,
  }: { method?: "POST" | "DELETE"; onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  const send = (token: string | null) =>
    new Promise<{ status: number; body: unknown }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${env.apiBaseUrl}${path}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-Client", "web");
      const sid = sessionId();
      if (sid) xhr.setRequestHeader("X-Session-ID", sid);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          body = null;
        }
        resolve({ status: xhr.status, body });
      };
      xhr.onerror = () => resolve({ status: 0, body: null });
      xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
      signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.send(form);
    });
  return (async () => {
    let res = await send(tokenStore.get());
    if (res.status === 401) {
      const { refreshAccessToken } = await import("@/api/client");
      const token = await refreshAccessToken();
      if (token) res = await send(token);
    }
    if (res.status >= 200 && res.status < 300) return res.body as T;
    throw toApiError(res.status, res.body);
  })();
}
